import { gql } from '@apollo/client';

import {
  WITH_COUNT_VARIABLES,
  WITH_COUNT_ARGUMENTS
} from '../queries/withCount';

export const ASSET_CORE_FIELDS = gql`
  fragment AssetCoreFields on Asset {
    id
    name
    category
    subCategory
    vin
    year
    make
    model
    correctedLotNumber
    stockNumber
    miles
    engineType
    listPrice
    soarrVideoUrls
    street
    city
    state
    postalCode { formatted, value }
    segment
    notes
    inspectionUrl
    nisInspectionUrl
    activeSaleAsset {
      id
      highBid
      highPreBidPrice
      sale {
        id
        saleOn
        startAt
        endAt
        status
        bidding
      }
    }
    location {
      id
      name
      street
      city
      state
      postalCode { formatted, value }
      phoneNumber { formatted, value }
    }
  }
`;

// Each view requests only the sizes it renders, rather than all four.
export const GRID_PHOTO_FIELDS = gql`
  fragment GridPhotoFields on Asset {
    photos {
      id
      filename
      contentType
      url
      card: url(size: CARD)
    }
    soarrPhotos {
      card: url(size: CARD)
    }
  }
`;

export const DETAIL_PHOTO_FIELDS = gql`
  fragment DetailPhotoFields on Asset {
    photos {
      id
      filename
      contentType
      url
      thumbnail: url(size: THUMBNAIL)
      detail: url(size: DETAIL)
    }
    soarrPhotos {
      thumbnail: url(size: THUMBNAIL)
      detail: url(size: DETAIL)
    }
  }
`;

export const ASSET_NAV_LIST = gql`
  query (
    $sort: String
    $sortDirection: String
    $search: String
    $filterColumns: [String!]
    $filterNames: [JSON!]
    $scopes: [String!]
    $saleId: ID
  ) {
    assetsWithCount(
      sort: $sort
      sortDirection: $sortDirection
      search: $search
      filterColumns: $filterColumns
      filterNames: $filterNames
      scopes: $scopes
      saleId: $saleId
    ) {
      entries { id name }
      count
    }
  }
`;

export const ASSET = gql`
  ${ASSET_CORE_FIELDS}
  ${DETAIL_PHOTO_FIELDS}
  query InventoryAsset($id: ID, $soarrId: ID) {
    asset: inventoryAsset(id: $id, soarrId: $soarrId) {
      ...AssetCoreFields
      ...DetailPhotoFields
      generalDetails
      tireDetails
      brakeDetails
      conditionDetails
      preBidData: activeSaleAsset {
        highPreBidPrice
        highPreBid { id, price, type }
        myPreBid { id, price, type }
        preBidStartPrice
        preBidDefaultStep
        preBidMinStep

        # ✅ NEW: viewer-specific max-bid state (nice to have for inventory UI too)
        myMaxBidPrice
        myMaxBidSurpassed
      }
      buyerUserAssetView {
        id
        watching
        emailAlert
        textAlert
      }
    }
  }
`;

const ASSET_FIELDS = `
  id
  name
  category
  year
  make
  model
  stockNumber
  miles
  engineType
  category
  listPrice
`;

export const ASSETS_WITH_COUNT = gql`
  ${ASSET_CORE_FIELDS}
  ${GRID_PHOTO_FIELDS}
  query (
    ${WITH_COUNT_VARIABLES}
    $saleId: ID
  ) {
    assetsWithCount(
      ${WITH_COUNT_ARGUMENTS}
      saleId: $saleId
    ) {
      entries {
        ...AssetCoreFields
        ...GridPhotoFields
      }
      count
    }
  }
`;

export const CREATE_PRE_BID = gql`
  mutation CreatePreBid(
    $saleAssetId: ID!
    $type: String!
    $price: Int!
  ) {
    createPreBid(
      saleAssetId: $saleAssetId
      type: $type
      price: $price
    ) {
      preBid {
        id
        price
        type

        # Apollo merges only the fields selected here into the normalized
        # SaleAsset, so anything the inventory cards read has to be listed or
        # they keep rendering the pre-bid state from before this mutation.
        saleAsset {
          id
          highBid
          highPreBidPrice
          myMaxBidPrice
          myMaxBidSurpassed
        }
      }
      errors { path, message }
    }
  }
`;

export const BUYER_USER_ASSET_WATCH = gql`
  mutation BuyerUserAssetWatch(
    $assetId: ID!
    $watching: Boolean!
    $emailAlert: Boolean!
    $textAlert: Boolean
  ) {
    buyerUserAssetWatch(
      assetId: $assetId
      watching: $watching
      emailAlert: $emailAlert
      textAlert: $textAlert
    ) {
      buyerUserAssetView {
        id
        watching
        emailAlert
        textAlert
      }
    }
  }
`;
